/**
 * As regras de cada passo do wizard — porta `cteatf_validacao.dart`.
 * Toda função devolve `null` quando pode avançar, e a mensagem exata da tela
 * quando não.
 */
import { kmPiso } from './valor';

export function erroDoCondutor({
  idCondutorSgtf, idUsuarioCondutor, tomaNome, tomaCpfCnpj, isTomaJuridicaSemCpf,
}: {
  idCondutorSgtf: string | null;
  idUsuarioCondutor: number | null;
  tomaNome: string;
  tomaCpfCnpj: string;
  isTomaJuridicaSemCpf: boolean;
}): string | null {
  if (idCondutorSgtf == null) return 'Selecione um condutor';
  if (idUsuarioCondutor == null) {
    return 'Não foi possível identificar o cadastro do condutor. Contate o suporte.';
  }
  const nome = tomaNome !== '' ? tomaNome : 'O condutor selecionado';
  if (isTomaJuridicaSemCpf || tomaCpfCnpj === '') {
    return `${nome} não tem CPF no cadastro — não é possível emitir no nome dele.`;
  }
  return null;
}

interface VeiculoWizard {
  idVeiculoSgtf: string;
  bloqueado: boolean;
}

/**
 * Lista vazia = "nenhum veículo do grupo está apto" (ADR-0005: não existe
 * mais condutor bloqueado por falta de veículo — só o veículo bloqueia).
 */
export function erroDoVeiculo({
  veiculosParaExibir, cargaIncompleta, idVeiculoSgtf, idVeiculoCte, placa, bloqueioMsg,
}: {
  veiculosParaExibir: VeiculoWizard[];
  cargaIncompleta: boolean;
  idVeiculoSgtf: string | null;
  idVeiculoCte: number | null;
  placa: string;
  bloqueioMsg: string | null;
}): string | null {
  if (veiculosParaExibir.length === 0) {
    if (cargaIncompleta) return 'Não foi possível carregar os veículos. Toque em "Tentar novamente".';
    return 'Nenhum veículo do seu grupo está liberado para emissão. Contate o suporte.';
  }
  if (veiculosParaExibir.every(v => v.bloqueado)) {
    return bloqueioMsg ?? 'Nenhum veículo está liberado para emissão no momento.';
  }
  if (idVeiculoSgtf == null || idVeiculoCte == null) return 'Selecione um veículo';
  const sel = veiculosParaExibir.find(v => v.idVeiculoSgtf === idVeiculoSgtf);
  if (sel?.bloqueado) return `O veículo ${placa} está bloqueado para emissão. Selecione outro veículo.`;
  return null;
}

export function erroDaRota({
  municipioOrigemIndice, municipioOrigemAtfOk, municipioDestinoIndice, municipioDestinoAtfOk, cMunIni, cMunFim,
}: {
  municipioOrigemIndice: number | null;
  municipioOrigemAtfOk: boolean;
  municipioDestinoIndice: number | null;
  municipioDestinoAtfOk: boolean;
  cMunIni: string;
  cMunFim: string;
}): string | null {
  if (municipioOrigemIndice == null) return 'Selecione o município de origem na lista de sugestões';
  if (!municipioOrigemAtfOk) return 'Município de origem não disponível para ATF. Selecione outro.';
  if (municipioDestinoIndice == null) return 'Selecione o município de destino na lista de sugestões';
  if (!municipioDestinoAtfOk) return 'Município de destino não disponível para ATF. Selecione outro.';
  if (cMunIni === '' || cMunFim === '') return 'Municípios de origem e destino são obrigatórios';
  return null;
}

/** O piso é comparado na precisão que o campo exibe (`kmPiso`), não na base crua da rota. */
export function erroDoTrajeto({
  kmIda, kmVolta, kmIdaBase, kmVoltaBase, somenteIda, permitirReduzirKm, itinerario,
}: {
  kmIda: number;
  kmVolta: number;
  kmIdaBase: number;
  kmVoltaBase: number;
  somenteIda: boolean;
  permitirReduzirKm: boolean;
  itinerario: string;
}): string | null {
  if (kmIda <= 0) return 'KM de ida deve ser maior que zero';
  if (!somenteIda && kmVolta <= 0) return 'KM de volta deve ser maior que zero';
  const pisoIda = kmPiso(kmIdaBase);
  if (!permitirReduzirKm && pisoIda > 0 && kmIda < pisoIda) {
    return `KM de ida não pode ser menor que o mínimo do trajeto (${pisoIda.toFixed(0)} km)`;
  }
  const pisoVolta = kmPiso(kmVoltaBase);
  if (!somenteIda && !permitirReduzirKm && pisoVolta > 0 && kmVolta < pisoVolta) {
    return `KM de volta não pode ser menor que o mínimo do trajeto (${pisoVolta.toFixed(0)} km)`;
  }
  if (itinerario === '') return 'Itinerário é obrigatório';
  return null;
}

interface PassageiroWizard {
  nome: string;
  docIdentificacao: string;
  orgaoExpedidor: string;
}

/** Os três campos são obrigatórios em TODOS os passageiros — exigência do portal do DER. */
export function erroDosPassageiros(passageiros: PassageiroWizard[]): string | null {
  if (passageiros.length === 0) return 'Adicione pelo menos um passageiro';
  for (const p of passageiros) {
    if (!p.nome) return 'Nome do passageiro é obrigatório';
    if (!p.docIdentificacao) return 'Documento do passageiro é obrigatório';
    if (!p.orgaoExpedidor) return 'Órgão expedidor é obrigatório';
  }
  return null;
}
